import { useEffect, useMemo, useRef, useState } from "react";
import { api, unwrap } from "@/lib/api";
import { GlossLogomark } from "@/components/GlossLogomark";
import { useSettings } from "@/contexts/SettingsContext";
import {
  Wifi, Scissors, Sparkles, Brush, Hand, Droplet, Sun, Eye, Heart,
  Star, Gift, Tag, Layers, QrCode,
} from "lucide-react";
import * as LucideIcons from "lucide-react";
import { DISPLAY } from "@/constants/testIds";
import { QRCodeSVG } from "qrcode.react";

// =========================================================
// Beauty tips (Ken-Burns photo slides)
// =========================================================
const TIPS = [
  { type: "tip", title: "Hydrate from within", body: "Drink 2.5 L of water daily — the easiest, free upgrade your hair and skin will thank you for.", img: "https://images.unsplash.com/photo-1530653333484-8c8a48a05dbc?w=1600&q=80", icon: Droplet, color: "#7C3AED" },
  { type: "tip", title: "SPF every single day", body: "Even on cloudy days. UVA penetrates clouds and is the leading cause of premature ageing.",     img: "https://images.unsplash.com/photo-1571781565036-d3f759be73e4?w=1600&q=80",         icon: Sun,     color: "#F59E0B" },
  { type: "tip", title: "Nail care basics",     body: "File in one direction, never saw back and forth. Cuticles are protective — push, don't cut.",   img: "https://images.unsplash.com/photo-1604654894610-df63bc536371?w=1600&q=80",         icon: Hand,    color: "#7C5C7A" },
  { type: "tip", title: "Skincare in layers",   body: "Thinnest to thickest: toner → essence → serum → moisturiser → SPF. Wait 60s between layers.",   img: "https://images.unsplash.com/photo-1487412947147-5cebf100ffc2?w=1600&q=80",         icon: Sparkles, color: "#D4A853" },
  { type: "tip", title: "Scalp first, ends last", body: "Shampoo your scalp; let the lather cleanse the lengths as it rinses through.",                img: "https://images.unsplash.com/photo-1605497788044-5a32c7078486?w=1600&q=80",         icon: Brush,   color: "#16A34A" },
  { type: "tip", title: "Hand-care daily",      body: "Cuticle oil at night, hand cream after every wash. Hands age fastest because we forget them.",  img: "https://images.unsplash.com/photo-1614859275375-fad04c1d2ea2?w=1600&q=80",          icon: Hand,    color: "#B5892E" },
  { type: "tip", title: "Lip care matters",     body: "Exfoliate twice a week with sugar + honey, then seal with balm. Don't lick — saliva dehydrates.", img: "https://images.unsplash.com/photo-1599733589046-8a35aeedf63a?w=1600&q=80",       icon: Heart,   color: "#B91C1C" },
  { type: "tip", title: "Eye care",             body: "Pat eye cream with your ring finger — its lightest pressure protects the delicate skin around your eyes.", img: "https://images.unsplash.com/photo-1591348122449-02525d70379b?w=1600&q=80", icon: Eye, color: "#5E4060" },
];

function maskName(name) {
  if (!name) return "Guest";
  const parts = name.trim().split(" ");
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1][0]}.`;
}

function iconForService(name = "") {
  const s = name.toLowerCase();
  if (s.includes("hair")) return Scissors;
  if (s.includes("nail") || s.includes("mani") || s.includes("pedi")) return Hand;
  if (s.includes("facial") || s.includes("skin")) return Sparkles;
  if (s.includes("massage") || s.includes("spa")) return Heart;
  return Sparkles;
}

// Build the public origin for QR codes (production = glossos.in, preview = current host)
function publicOrigin() {
  const env = process.env.REACT_APP_PUBLIC_ORIGIN;
  if (env) return env.replace(/\/$/, "");
  if (typeof window !== "undefined") return window.location.origin;
  return "https://glossos.in";
}

// =========================================================
// Main component
// =========================================================
export default function WaitingDisplay() {
  const { settings } = useSettings();
  const [data, setData] = useState([]);
  const [clock, setClock] = useState(new Date());
  const [slideIdx, setSlideIdx] = useState(0);
  const [stats, setStats] = useState(null);
  const [activePackage, setActivePackage] = useState(null);
  const [flashes, setFlashes] = useState([]);
  const [flashIdx, setFlashIdx] = useState(0);

  // Token-call overlay state
  const previousInServiceRef = useRef(new Set());
  const overlayQueueRef = useRef([]);
  const [overlay, setOverlay] = useState(null);

  // ----------------------------------------
  // Slides — interleave promos into beauty tips
  // ----------------------------------------
  const slides = useMemo(() => {
    const promosEnabled = settings.display_promo_enabled !== false;
    const promos = promosEnabled ? buildPromos({ activePackage, settings }) : [];
    if (promos.length === 0) return TIPS;
    // Insert promos at positions 2 and 5 of the rotation
    const out = [...TIPS];
    if (promos[0]) out.splice(Math.min(2, out.length), 0, promos[0]);
    if (promos[1]) out.splice(Math.min(5, out.length), 0, promos[1]);
    if (promos[2]) out.push(promos[2]);
    if (promos[3]) out.push(promos[3]);
    return out;
  }, [activePackage, settings]);

  // ----------------------------------------
  // Polling — appointments + stats + active package
  // ----------------------------------------
  useEffect(() => {
    let active = true;
    const fetchOnce = async () => {
      try {
        const r = await api.get("/appointments/public", { params: { branch_id: "branch-001" } });
        if (active) setData(unwrap(r) || []);
      } catch {}
      try {
        const s = await api.get("/stats/today", { params: { branch_id: "branch-001" } });
        if (active) setStats(unwrap(s) || null);
      } catch {}
    };
    const fetchPackage = async () => {
      try {
        const p = await api.get("/packages", { params: { active_only: true, branch_id: "branch-001" } });
        const list = unwrap(p) || [];
        if (active && list.length) setActivePackage(list[0]);
      } catch {}
    };
    const fetchFlashes = async () => {
      try {
        const f = await api.get("/marketing-flashes", { params: { active_only: true, branch_id: "branch-001" } });
        if (active) setFlashes(unwrap(f) || []);
      } catch {}
    };
    fetchOnce();
    fetchPackage();
    fetchFlashes();
    const i = setInterval(fetchOnce, 5000);
    const pi = setInterval(fetchPackage, 5 * 60 * 1000);
    const fi = setInterval(fetchFlashes, 60 * 1000);   // refresh flashes every minute
    const c = setInterval(() => setClock(new Date()), 1000);
    const t = setInterval(() => setSlideIdx((x) => (x + 1) % Math.max(slides.length, 1)), 8000);
    const ft = setInterval(() => setFlashIdx((x) => x + 1), 6000);   // rotate flash every 6s
    return () => { active = false; clearInterval(i); clearInterval(pi); clearInterval(fi); clearInterval(c); clearInterval(t); clearInterval(ft); };
  }, [slides.length]);

  // ----------------------------------------
  // Token-call overlay detection — diff polls for new in-service tokens
  // ----------------------------------------
  useEffect(() => {
    const currentInService = data.filter((d) => d.status === "in-service");
    const currentTokens = new Set(currentInService.map((a) => a.token_number));
    const prev = previousInServiceRef.current;
    const newOnes = currentInService.filter((a) => !prev.has(a.token_number));
    if (newOnes.length > 0) {
      newOnes.forEach((a) => overlayQueueRef.current.push(a));
      drainOverlay();
    }
    previousInServiceRef.current = currentTokens;
  }, [data]); // eslint-disable-line

  const drainOverlay = () => {
    if (overlay) return;   // wait until current one auto-dismisses
    const next = overlayQueueRef.current.shift();
    if (!next) return;
    setOverlay(next);
    try {
      // light celebratory beep using WebAudio — no asset needed
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.frequency.value = 880; o.type = "sine";
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.05);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.6);
      o.connect(g); g.connect(ctx.destination);
      o.start(); o.stop(ctx.currentTime + 0.7);
    } catch {}
    setTimeout(() => {
      setOverlay(null);
      // sequential drain — show the next queued overlay
      setTimeout(drainOverlay, 200);
    }, 8000);
  };

  // ----------------------------------------
  // Derived state
  // ----------------------------------------
  const nowServing = data.find((d) => d.status === "in-service");
  const nextUpAll = data.filter((d) => d.status === "checked-in");
  const nextUp = nextUpAll[0];
  const otherNext = nextUpAll.slice(1, 3);
  const booked = data.filter((d) => d.status === "booked");
  const waiting = data.filter((d) => ["checked-in", "booked"].includes(d.status));
  const avgWait = nextUpAll.length > 0 ? Math.max(8, 12 + nextUpAll.length * 6) : 0;

  const liveMessage = useMemo(() => {
    const w = waiting.length;
    if (w === 0) return { text: "Walk in now — no wait! ✨", tone: "success" };
    if (w <= 2) return { text: "Almost your turn — grab a seat 💛", tone: "warm" };
    if (w <= 5 && avgWait < 30) return { text: "Relax — we'll be with you shortly", tone: "calm" };
    if (w <= 10 && avgWait <= 60) return { text: "We recommend our complimentary tea while you wait ☕", tone: "warm" };
    return { text: "Thank you for your patience — we're moving as fast as we can 🙏", tone: "calm" };
  }, [waiting.length, avgWait]);

  const current = slides[slideIdx % slides.length] || TIPS[0];
  const checkinUrl = (token) => `${publicOrigin()}/checkin?token=${encodeURIComponent(token)}&branch_id=branch-001`;
  const bookingUrl = `${publicOrigin()}/book`;

  const showSatisfaction =
    typeof stats?.avg_feedback_today === "number" &&
    stats.avg_feedback_today >= 4 &&
    (stats.feedback_count_today || 0) >= 3;

  return (
    <div className="min-h-screen w-full overflow-hidden" data-testid={DISPLAY.root}>
      {/* Header */}
      <div className="px-8 lg:px-12 py-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <GlossLogomark size={56} animate={false} />
          <div>
            <div className="font-display font-bold text-[28px] text-[var(--color-text-primary)] tracking-[-0.02em] leading-none">{settings.salon_name}</div>
            <div className="font-inter text-[13px] text-[var(--color-text-muted)] mt-1">{settings.branch_name} · The operating system for modern salons</div>
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono text-[44px] leading-none text-[var(--color-text-primary)]" style={{ letterSpacing: "-0.02em" }}>
            {clock.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </div>
          <div className="font-inter text-[13px] text-[var(--color-text-muted)] mt-1">
            {clock.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" })}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_0.85fr] gap-7 px-8 lg:px-12 pb-10">
        {/* ===================================================
            LEFT — Queue board
            =================================================== */}
        <div className="space-y-5">
          {/* Now Serving + live progress bar */}
          <NowServingPanel a={nowServing} />

          {/* You're Next (with QR self-check-in) */}
          <YoureNextCard
            appointment={nextUp}
            others={otherNext}
            checkinUrl={nextUp ? checkinUrl(nextUp.token_number) : null}
          />

          {/* Count tiles */}
          <div className="grid grid-cols-3 gap-3">
            <CountTile label="Waiting"    value={waiting.length}     />
            <CountTile label="Checked in" value={nextUpAll.length}   />
            <CountTile label="Est. wait"  value={`~${avgWait}m`}     />
          </div>

          {/* Live wait personalisation */}
          <LiveWaitMessage message={liveMessage} />

          {/* Full waiting list */}
          <div className="glass-subtle rounded-2xl p-5">
            <div className="overline mb-3">Waiting list</div>
            {waiting.length === 0 ? (
              <div className="font-inter text-[13px] text-[var(--color-text-muted)]">No one waiting.</div>
            ) : (
              <div className="space-y-2">
                {waiting.slice(0, 6).map((a, i) => {
                  const Icon = iconForService((a.services_requested || [])[0]);
                  return (
                    <div key={a.token_number} className="flex items-center gap-3 py-1.5">
                      <div className="font-sora font-bold text-[12px] text-[var(--color-text-muted)] w-6">#{i + 1}</div>
                      <div className="font-mono text-[16px] text-[var(--color-mauve-deep)] min-w-[58px]">{a.token_number}</div>
                      <div className="flex-1 font-sora font-semibold text-[13.5px] text-[var(--color-text-primary)]">{a.masked_name}</div>
                      <Icon size={14} className="text-[var(--color-text-muted)]" />
                      <div className="font-inter text-[11.5px] text-[var(--color-text-muted)] hidden sm:block">{(a.services_requested || [])[0]}</div>
                    </div>
                  );
                })}
              </div>
            )}
            {booked.length > 0 && (
              <>
                <div className="my-3 border-t border-dashed border-[var(--color-text-faint)]/40" />
                <div className="overline text-[10px]">Not yet arrived · {booked.length}</div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {booked.slice(0, 8).map((a) => (
                    <span key={a.token_number} className="px-2.5 py-1 rounded-full bg-white/60 font-mono text-[11.5px] text-[var(--color-text-muted)] border border-white/80">
                      {a.token_number}
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* "Book your next visit" QR card */}
          <BookNextQRCard url={bookingUrl} />
        </div>

        {/* ===================================================
            RIGHT — Beauty tip / promos + bottom tiles
            =================================================== */}
        <div className="space-y-5">
          <SlideStage current={current} totalSlides={slides.length} idx={slideIdx} />

          <div className="grid grid-cols-2 gap-3">
            <MarketingFlashTile flashes={flashes} idx={flashIdx} />
            <div className="glass-subtle rounded-2xl p-5">
              <div className="flex items-center gap-2">
                <Wifi size={16} className="text-[var(--color-mauve)]" />
                <span className="overline">Wi-Fi</span>
              </div>
              <div className="font-sora font-bold text-[18px] mt-1">GLOSS-Guest</div>
              <div className="font-inter text-[12px] text-[var(--color-text-muted)] mt-1">Open · no password</div>
            </div>
          </div>

          {showSatisfaction && <SatisfactionTile score={stats.avg_feedback_today} count={stats.feedback_count_today} salon={settings.salon_name} />}
        </div>
      </div>

      {/* Animated token-call overlay */}
      {overlay && <TokenCallOverlay a={overlay} />}

      <div className="px-8 lg:px-12 pb-6 flex items-center justify-between font-inter text-[12px] text-[var(--color-text-muted)]">
        <div>Refreshes every 5 seconds · No sign-in required</div>
        <div className="flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full bg-[var(--color-success)] live-dot" />
          Live · {clock.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
        </div>
      </div>
    </div>
  );
}

// =========================================================
// Now-serving panel with live service progress bar
// =========================================================
function NowServingPanel({ a }) {
  const [, force] = useState(0);
  useEffect(() => {
    if (!a?.service_started_at) return;
    const i = setInterval(() => force((x) => x + 1), 5000);
    return () => clearInterval(i);
  }, [a?.service_started_at]);

  const progress = useMemo(() => {
    if (!a?.service_started_at || !a?.service_duration_minutes) return null;
    const started = new Date(a.service_started_at).getTime();
    const elapsedMin = Math.max(0, (Date.now() - started) / 60000);
    const dur = a.service_duration_minutes || 30;
    const buffer = a.service_buffer_minutes || 10;
    const pct = Math.min((elapsedMin / dur) * 100, 100);
    let tone = "normal";   // gold → amber → red
    let label;
    if (elapsedMin > dur + buffer) { tone = "late"; label = "Running a little late"; }
    else if (elapsedMin > dur) { tone = "over"; label = "Finishing up…"; }
    else label = `${Math.max(1, Math.round(dur - elapsedMin))} min remaining`;
    return { pct, tone, label, elapsedMin: Math.round(elapsedMin), dur };
  }, [a]);

  return (
    <div
      className="relative overflow-hidden rounded-[28px] p-7 text-white"
      style={{
        background:
          "radial-gradient(ellipse 60% 80% at 80% 20%, rgba(212,168,83,0.35) 0%, transparent 65%)," +
          "linear-gradient(180deg, #5E4060 0%, #3F2A45 100%)",
        boxShadow: "0 30px 60px -20px rgba(94,64,96,0.45)",
      }}
    >
      <div className="flex items-start justify-between">
        <div className="overline text-white/70" style={{ color: "rgba(255,255,255,0.65)" }}>Now being served</div>
        <span className="status-pill" style={{ background: "rgba(212,168,83,0.2)", color: "#F0D898" }}>
          <span className="dot" style={{ background: "#F0D898", animation: "pulseDot 1.4s infinite" }} />
          IN SERVICE
        </span>
      </div>
      {a ? (
        <>
          <div className="mt-4 flex items-end gap-6 flex-wrap">
            <div className="font-mono font-medium text-[90px] leading-[0.9]" style={{ color: "#F0D898", letterSpacing: "-0.04em" }} data-testid={DISPLAY.nowServingToken}>
              {a.token_number}
            </div>
            <div className="pb-3 flex-1 min-w-[200px]">
              <div className="font-display text-[28px] font-semibold leading-tight">{maskName(a.masked_name)}</div>
              <div className="font-inter text-[15px] text-white/75 mt-1">{(a.services_requested || []).join(" + ")}</div>
              <div className="font-inter text-[13px] text-white/65 mt-2">with {a.assigned_stylist_name} · {a.chair_room}</div>
            </div>
          </div>

          {progress && (
            <div className="mt-4" data-testid="display-progress-bar">
              <div className="h-2 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.10)" }}>
                <div
                  className="h-full transition-all"
                  style={{
                    width: `${progress.pct}%`,
                    background: progress.tone === "late"
                      ? "linear-gradient(90deg, #DC2626, #B91C1C)"
                      : progress.tone === "over"
                      ? "linear-gradient(90deg, #F59E0B, #D97706)"
                      : "linear-gradient(90deg, #F0D898, #D4A853)",
                    transition: "width 5s linear, background 400ms",
                  }}
                />
              </div>
              <div className="flex items-center justify-between mt-1.5 font-inter text-[11.5px]" style={{ color: "rgba(255,255,255,0.75)" }}>
                <span>{Math.round(progress.pct)}% complete</span>
                <span style={{ color: progress.tone === "late" ? "#FCA5A5" : progress.tone === "over" ? "#FDE68A" : "rgba(255,255,255,0.75)" }}>{progress.label}</span>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="mt-6 font-inter text-white/70 text-[15px]">No one is currently being served.</div>
      )}
    </div>
  );
}

// =========================================================
// "YOU'RE NEXT" card with QR self-check-in
// =========================================================
function YoureNextCard({ appointment, others, checkinUrl }) {
  if (!appointment) {
    return (
      <div className="rounded-[24px] p-6 glass-elevated"
           style={{ background: "linear-gradient(180deg,rgba(255,247,228,0.85) 0%, rgba(248,225,221,0.85) 100%)" }}>
        <div className="overline mb-3">Next up</div>
        <div className="font-inter text-[14px] text-[var(--color-text-muted)]">Quiet for a moment ✨</div>
      </div>
    );
  }
  return (
    <div className="rounded-[24px] p-6 relative overflow-hidden anim-pulse-gold" data-testid="display-youre-next-card"
         style={{
           background: "linear-gradient(135deg, rgba(255,247,228,0.95) 0%, rgba(248,225,221,0.95) 100%)",
           border: "2px solid transparent",
           boxShadow: "0 0 0 1px rgba(212,168,83,0.35), 0 20px 40px -16px rgba(212,168,83,0.30)",
         }}>
      <div className="flex items-center justify-between mb-3">
        <div className="overline" style={{ color: "#8E6A1A" }}>You're next</div>
        <span className="status-pill" style={{ background: "rgba(212,168,83,0.18)", color: "#8E6A1A", padding: "3px 10px", fontSize: 10 }}>
          <span className="dot" style={{ background: "#D4A853", animation: "pulseDot 1.4s infinite" }} />
          GET READY
        </span>
      </div>
      <div className="grid grid-cols-[1fr_auto] gap-5 items-center">
        <div>
          <div className="font-mono text-[64px] leading-none text-[var(--color-gold-deeper)]" style={{ letterSpacing: "-0.03em" }} data-testid="display-youre-next-token">
            {appointment.token_number}
          </div>
          <div className="font-display text-[20px] font-semibold text-[var(--color-text-primary)] mt-2">{appointment.masked_name}</div>
          <div className="font-inter text-[13.5px] text-[var(--color-text-secondary)] mt-0.5">
            {(appointment.services_requested || []).join(" + ")}
          </div>
        </div>
        <div className="text-center" data-testid="display-checkin-qr">
          <div className="rounded-xl p-2 inline-block" style={{ background: "#FDF9F4", border: "1px solid rgba(212,168,83,0.30)" }}>
            <QRCodeSVG value={checkinUrl} size={128} bgColor="#FDF9F4" fgColor="#D4A853" level="M" />
          </div>
          <div className="font-inter text-[11.5px] text-[var(--color-text-muted)] mt-1.5 max-w-[140px] mx-auto">
            Scan to confirm you're here
          </div>
        </div>
      </div>

      {others.length > 0 && (
        <div className="grid grid-cols-2 gap-3 pt-3 mt-3 border-t border-[var(--color-gold-mid)]/20">
          {others.map((a) => (
            <div key={a.token_number} className="flex items-center gap-3">
              <div className="font-mono text-[28px] leading-none text-[var(--color-gold-dark)]">{a.token_number}</div>
              <div className="leading-tight">
                <div className="font-sora font-semibold text-[13.5px]">{a.masked_name}</div>
                <div className="font-inter text-[11.5px] text-[var(--color-text-muted)]">{(a.services_requested || [])[0]}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// =========================================================
// Live wait message
// =========================================================
function LiveWaitMessage({ message }) {
  return (
    <div className="rounded-2xl px-5 py-3 text-center anim-fade-in"
         data-testid="display-wait-message"
         style={{
           background: "linear-gradient(90deg, rgba(212,168,83,0.10) 0%, rgba(212,168,83,0.06) 50%, rgba(212,168,83,0.10) 100%)",
           border: "1px solid rgba(212,168,83,0.28)",
         }}>
      <div className="font-inter text-[15px] text-[var(--color-gold-deeper)] font-medium">{message.text}</div>
    </div>
  );
}

// =========================================================
// "Book next visit" QR card
// =========================================================
function BookNextQRCard({ url }) {
  return (
    <div className="glass-subtle rounded-2xl p-5 flex items-center justify-between gap-5" data-testid="display-book-qr-card">
      <div className="min-w-0">
        <div className="overline" style={{ color: "#5E4060" }}>Loved your visit?</div>
        <div className="font-display text-[22px] font-bold text-[var(--color-text-primary)] tracking-[-0.02em] mt-1">Book your next appointment</div>
        <div className="font-inter text-[13px] text-[var(--color-text-muted)] mt-1">Scan to book online · No app needed</div>
      </div>
      <div className="text-center shrink-0">
        <div className="rounded-xl p-2 inline-block" style={{ background: "#FDF9F4", border: "1px solid rgba(212,168,83,0.30)" }}>
          <QRCodeSVG value={url} size={160} bgColor="#FDF9F4" fgColor="#D4A853" level="M" />
        </div>
        <div className="font-mono text-[11px] text-[var(--color-text-muted)] mt-1.5">{url.replace(/^https?:\/\//, "")}</div>
      </div>
    </div>
  );
}

// =========================================================
// Promo slide builder
// =========================================================
function buildPromos({ activePackage, settings }) {
  const promos = [
    {
      type: "promo",
      kind: "service_of_month",
      title: "Keratin Treatment",
      bg: "linear-gradient(135deg, #D4A853 0%, #8E6A1A 100%)",
      color: "white",
    },
    {
      type: "promo",
      kind: "loyalty",
      title: "Earn while you glow",
      bg: "linear-gradient(135deg, #5E4060 0%, #3F2A45 100%)",
      color: "white",
    },
    {
      type: "promo",
      kind: "referral",
      title: "Love GLOSS? Share it.",
      bg: "linear-gradient(135deg, #F8E1DD 0%, #F0D5CE 100%)",
      color: "#5E4060",
    },
  ];
  if (activePackage) {
    promos.push({
      type: "promo",
      kind: "package",
      title: activePackage.name,
      package: activePackage,
      bg: "linear-gradient(135deg, #FFF7E4 0%, #F0D898 100%)",
      color: "#3F2A45",
    });
  }
  return promos;
}

// =========================================================
// Slide stage — renders either a beauty tip (photo) or a promo
// =========================================================
function SlideStage({ current, totalSlides, idx }) {
  if (current?.type === "promo") {
    return (
      <div className="relative overflow-hidden rounded-[28px] h-[480px] anim-fade-in" data-testid="display-promo-slide" style={{ background: current.bg }}>
        <SlideProgressDots total={totalSlides} idx={idx} />
        <PromoBody slide={current} />
      </div>
    );
  }
  return (
    <div className="relative overflow-hidden rounded-[28px] h-[480px]">
      <img
        key={idx}
        src={current.img}
        alt={current.title}
        className="absolute inset-0 w-full h-full object-cover"
        style={{ animation: "kenBurns 8s ease-out forwards" }}
      />
      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(28,25,23,0.15) 0%, rgba(28,25,23,0.65) 100%)" }} />
      <div className="absolute top-5 left-5 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full" style={{ background: "rgba(0,0,0,0.4)", backdropFilter: "blur(8px)" }}>
        <current.icon size={14} style={{ color: current.color }} />
        <span className="font-sora font-bold text-[10.5px] uppercase tracking-[0.25em] text-white">Beauty tip</span>
      </div>
      <SlideProgressDots total={totalSlides} idx={idx} />
      <div className="absolute bottom-0 inset-x-0 p-5">
        <div className="rounded-2xl p-5" style={{ background: "rgba(255,255,255,0.10)", backdropFilter: "blur(28px) saturate(160%)", border: "1px solid rgba(255,255,255,0.25)" }}>
          <div className="font-display font-bold text-white text-[28px] md:text-[36px] leading-tight tracking-[-0.02em]">{current.title}</div>
          <div className="font-inter text-white/92 text-[15px] mt-2 leading-relaxed">{current.body}</div>
        </div>
      </div>
    </div>
  );
}

function SlideProgressDots({ total, idx }) {
  return (
    <div className="absolute top-5 right-5 flex gap-1.5">
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} className="h-1.5 rounded-full transition-all" style={{
          width: i === (idx % total) ? 22 : 6,
          background: i === (idx % total) ? "white" : "rgba(255,255,255,0.45)",
        }} />
      ))}
    </div>
  );
}

function PromoBody({ slide }) {
  const k = slide.kind;
  if (k === "service_of_month") {
    return (
      <div className="absolute inset-0 flex flex-col justify-center px-10">
        <div className="font-sora font-bold text-[11px] uppercase tracking-[0.35em] text-white/80">This month</div>
        <div className="font-display font-bold text-[56px] leading-[0.95] text-white tracking-[-0.02em] mt-3">KERATIN<br />TREATMENT</div>
        <div className="font-mono text-[40px] mt-4" style={{ color: "#FFF7E4" }}>₹2,500</div>
        <div className="font-inter text-[14px] text-white/85 mt-3">Ask our team for details</div>
        <div className="absolute bottom-6 left-10 right-10 flex items-center justify-between font-inter text-[11.5px] text-white/70">
          <span>GLOSS · Lumière Salon & Spa</span>
          <Sparkles size={14} />
        </div>
      </div>
    );
  }
  if (k === "loyalty") {
    return (
      <div className="absolute inset-0 flex flex-col justify-center px-10 text-center">
        <Star size={48} className="mx-auto" style={{ color: "#F0D898" }} fill="#F0D898" />
        <div className="font-display font-bold text-[36px] leading-tight text-white tracking-[-0.02em] mt-4">EARN WHILE YOU GLOW</div>
        <div className="font-inter text-[17px] text-white/90 mt-3">Every ₹1 spent = 1 loyalty point</div>
        <div className="font-inter text-[14px] text-white/80 mt-1">Redeem at your next visit</div>
        <div className="font-inter text-[12px] text-white/70 mt-5">Ask reception about your balance</div>
      </div>
    );
  }
  if (k === "referral") {
    return (
      <div className="absolute inset-0 flex flex-col justify-center px-10 text-center" style={{ color: slide.color }}>
        <Gift size={42} className="mx-auto" style={{ color: "#D4A853" }} />
        <div className="font-display font-bold text-[40px] leading-tight tracking-[-0.02em] mt-3" style={{ color: "#5E4060" }}>LOVE GLOSS?<br />SHARE IT.</div>
        <div className="font-inter text-[16px] mt-3" style={{ color: "#5E4060", opacity: 0.85 }}>Refer a friend — you both get ₹200 loyalty</div>
        <div className="font-inter text-[12.5px] mt-4" style={{ color: "#5E4060", opacity: 0.7 }}>Ask reception for your referral code</div>
      </div>
    );
  }
  if (k === "package") {
    const p = slide.package || {};
    return (
      <div className="absolute inset-0 flex flex-col justify-center px-10" style={{ color: slide.color }}>
        <div className="flex items-center gap-2 mb-2">
          <Tag size={14} style={{ color: "#8E6A1A" }} />
          <span className="font-sora font-bold uppercase text-[11px] tracking-[0.25em]" style={{ color: "#8E6A1A" }}>Package deal</span>
        </div>
        <div className="font-mono text-[48px] leading-none" style={{ color: "#8E6A1A" }}>SAVE ₹{p.savings || 0}</div>
        <div className="font-display font-bold text-[28px] leading-tight tracking-[-0.02em] mt-3" style={{ color: "#3F2A45" }}>{p.name}</div>
        <div className="flex flex-wrap gap-1.5 mt-3">
          {(p.services || []).slice(0, 5).map((s) => (
            <span key={s.service_name} className="px-2.5 py-1 rounded-full font-inter text-[11.5px]"
                  style={{ background: "rgba(212,168,83,0.18)", color: "#5E4060", border: "1px solid rgba(212,168,83,0.30)" }}>
              {s.service_name}
            </span>
          ))}
        </div>
        <div className="font-inter text-[13px] mt-4" style={{ color: "#5E4060", opacity: 0.85 }}>Ask reception to book this package</div>
      </div>
    );
  }
  return null;
}

// =========================================================
// Animated token-call overlay (full-screen)
// =========================================================
function TokenCallOverlay({ a }) {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center anim-fade-in" data-testid="display-token-call-overlay"
         style={{
           background: "radial-gradient(ellipse 60% 50% at 50% 40%, rgba(212,168,83,0.45) 0%, transparent 65%), linear-gradient(180deg, #5E4060 0%, #3F2A45 100%)",
         }}>
      <div className="text-center px-8 anim-slide-up-lg" style={{ animationDuration: "600ms" }}>
        <Scissors size={80} className="mx-auto" style={{ color: "#F0D898", animation: "spinOnce 1s ease-out" }} />
        <div className="font-mono leading-none mt-6" style={{ color: "white", fontSize: 120, letterSpacing: "-0.04em" }}>
          {a.token_number}
        </div>
        <div className="font-display font-bold text-[32px] text-white mt-4 tracking-[-0.02em]">{a.masked_name}</div>
        {(a.services_requested || []).length > 0 && (
          <div className="font-inter text-[18px] text-white/85 mt-1">{(a.services_requested || []).join(" + ")}</div>
        )}
        {(a.assigned_stylist_name || a.chair_room) && (
          <div className="font-inter text-[16px] text-white/65 mt-3">
            {a.assigned_stylist_name && <>with {a.assigned_stylist_name}</>}
            {a.assigned_stylist_name && a.chair_room && <> · </>}
            {a.chair_room && a.chair_room}
          </div>
        )}
        <div className="font-inter text-[14px] text-white/60 mt-8">Please make your way to the service area</div>
      </div>
      <style>{`
        @keyframes spinOnce { from { transform: rotate(0deg); opacity: 0.5; } to { transform: rotate(360deg); opacity: 1; } }
        @keyframes pulseGoldBorder {
          0%, 100% { box-shadow: 0 0 0 1px rgba(212,168,83,0.35), 0 20px 40px -16px rgba(212,168,83,0.30); }
          50% { box-shadow: 0 0 0 3px rgba(212,168,83,0.65), 0 20px 40px -16px rgba(212,168,83,0.50); }
        }
        .anim-pulse-gold { animation: pulseGoldBorder 2.4s ease-in-out infinite; }
      `}</style>
    </div>
  );
}

// =========================================================
// Satisfaction (social proof) tile
// =========================================================
function SatisfactionTile({ score, count, salon }) {
  const fullStars = Math.round(score);
  return (
    <div className="rounded-2xl p-5 anim-scale-in" data-testid="display-satisfaction-tile"
         style={{
           background: "linear-gradient(135deg, rgba(212,168,83,0.14) 0%, rgba(240,216,152,0.10) 100%)",
           border: "1px solid rgba(212,168,83,0.32)",
         }}>
      <div className="flex items-center gap-1 mb-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Star
            key={i}
            size={18}
            className="anim-scale-in"
            style={{
              color: i < fullStars ? "#D4A853" : "rgba(212,168,83,0.25)",
              animationDelay: `${i * 80}ms`,
            }}
            fill={i < fullStars ? "#D4A853" : "transparent"}
          />
        ))}
      </div>
      <div className="font-mono text-[28px] leading-none text-[var(--color-gold-deeper)]">{score.toFixed(1)}/5</div>
      <div className="font-inter text-[13px] text-[var(--color-text-secondary)] mt-1">by {count} happy client{count === 1 ? "" : "s"} today</div>
      <div className="font-sora font-medium text-[11.5px] text-[var(--color-text-muted)] mt-2">{salon}</div>
    </div>
  );
}

// =========================================================
// Count tile (left column)
// =========================================================
function CountTile({ label, value }) {
  return (
    <div className="glass-subtle rounded-2xl p-4 text-center">
      <div className="overline text-[10px] mb-1">{label}</div>
      <div className="font-mono text-[30px] leading-none text-[var(--color-text-primary)]">{value}</div>
    </div>
  );
}


// =========================================================
// Marketing Flash Tile — replaces the old Emergency 108 card.
// Rotates through active flashes every 6 seconds with fade.
// =========================================================
const FLASH_PALETTES = {
  gold:    { bg: "linear-gradient(135deg,#D4A853 0%,#8E6A1A 100%)", fg: "white",  chip: "rgba(255,255,255,0.20)", chipFg: "#FFF7E4" },
  mauve:   { bg: "linear-gradient(135deg,#5E4060 0%,#3F2A45 100%)", fg: "white",  chip: "rgba(212,168,83,0.25)",  chipFg: "#F0D898" },
  rose:    { bg: "linear-gradient(135deg,#F8E1DD 0%,#F0D5CE 100%)", fg: "#5E4060", chip: "rgba(94,64,96,0.12)",    chipFg: "#5E4060" },
  success: { bg: "linear-gradient(135deg,#16A34A 0%,#15803D 100%)", fg: "white",  chip: "rgba(255,255,255,0.20)", chipFg: "#DCFCE7" },
  amber:   { bg: "linear-gradient(135deg,#F59E0B 0%,#B45309 100%)", fg: "white",  chip: "rgba(255,255,255,0.20)", chipFg: "#FEF3C7" },
};

function MarketingFlashTile({ flashes, idx }) {
  const list = flashes || [];
  if (list.length === 0) {
    // Fallback when manager has not configured any flash — keep the slot useful
    return (
      <div className="rounded-2xl p-5 text-white" style={{ background: FLASH_PALETTES.gold.bg }} data-testid="display-flash-tile">
        <div className="flex items-center gap-2">
          <Tag size={16} />
          <span className="font-sora font-bold uppercase text-[11px] tracking-[0.25em]">Today's special</span>
        </div>
        <div className="font-display font-bold text-[24px] mt-1 leading-tight tracking-[-0.02em]">Ask reception about today's offers</div>
        <div className="font-inter text-[12px] text-white/85 mt-1">Member-only deals refresh weekly</div>
      </div>
    );
  }
  const flash = list[idx % list.length];
  const palette = FLASH_PALETTES[flash.accent] || FLASH_PALETTES.gold;
  const IconCmp = (flash.icon && LucideIcons[flash.icon]) ? LucideIcons[flash.icon] : Tag;

  return (
    <div
      key={flash.id}
      className="rounded-2xl p-5 anim-fade-in relative overflow-hidden"
      style={{ background: palette.bg, color: palette.fg }}
      data-testid="display-flash-tile"
    >
      <div className="flex items-center gap-2">
        <IconCmp size={16} />
        <span className="font-sora font-bold uppercase text-[11px] tracking-[0.25em]" style={{ opacity: 0.9 }}>Today's flash</span>
      </div>
      <div className="font-display font-bold text-[22px] mt-1.5 leading-tight tracking-[-0.02em]" data-testid="display-flash-title">
        {flash.title}
      </div>
      {flash.subtitle && (
        <div className="font-inter text-[12.5px] mt-1" style={{ opacity: 0.92 }}>{flash.subtitle}</div>
      )}
      {flash.code && (
        <div
          className="inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-full font-mono text-[12px]"
          style={{ background: palette.chip, color: palette.chipFg }}
        >
          <Tag size={11} /> {flash.code}
        </div>
      )}
      {list.length > 1 && (
        <div className="absolute bottom-2 right-3 flex gap-1">
          {list.map((_, i) => (
            <div key={i} className="rounded-full" style={{
              width: i === (idx % list.length) ? 10 : 4,
              height: 4,
              background: palette.fg,
              opacity: i === (idx % list.length) ? 0.95 : 0.35,
              transition: "all 240ms",
            }} />
          ))}
        </div>
      )}
    </div>
  );
}
